import { useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../components/button";
import { PageHeader } from "../../../components/common";
import { useToast } from "../../../components/toast-container";
import {
  CampaignEditorForm,
  CampaignEditorFormHandle,
  CampaignEditorFormValues,
} from "../components/CampaignEditorForm";
import { CAMPAIGN_STATUSES } from "../types/campaign-status.constants";
import { useCampaignBuilder } from "../hooks/useCampaignBuilder";

function UpdateCampaignPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { id } = useParams();
  const formRef = useRef<CampaignEditorFormHandle | null>(null);

  const campaignId = id || "";
  const { campaign, loading, error, saving, updateCampaign } = useCampaignBuilder(campaignId);

  const defaultValues = useMemo<CampaignEditorFormValues>(
    () => ({
      name: campaign?.name || "",
      description: campaign?.description || "",
      status: campaign?.status || CAMPAIGN_STATUSES.Unactive,
      startDate: campaign?.startDate?.slice(0, 10) || "",
      endDate: campaign?.endDate?.slice(0, 10) || "",
    }),
    [campaign],
  );

  const handleBack = useCallback(() => {
    navigate("/campaign");
  }, [navigate]);

  const handleSave = useCallback(() => {
    formRef.current?.submit();
  }, []);

  const handleSubmit = useCallback(
    async (values: CampaignEditorFormValues) => {
      if (!campaignId) {
        return;
      }

      await updateCampaign({
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        status: values.status,
        startDate: values.startDate ? `${values.startDate}T00:00:00Z` : undefined,
        endDate: values.endDate ? `${values.endDate}T00:00:00Z` : undefined,
      });

      addToast({
        type: "success",
        title: "Success!",
        message: "Campaign updated successfully",
      });
    },
    [addToast, campaignId, updateCampaign],
  );

  return (
    <div className="min-h-full bg-[linear-gradient(180deg,#F7FBF9_0%,#FFFFFF_22%)]">
      <div className="sticky top-0 z-30 border-b border-[#E5EFE9] bg-white/92 px-6 py-4 backdrop-blur">
        <div className="mx-auto max-w-7xl">
          <PageHeader
            title="Update Campaign"
            subtitle="Campaign Builder"
            sticky={false}
            actions={
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="brand-outline"
                  onClick={handleBack}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="brand-secondary"
                  onClick={handleSave}
                  disabled={loading || saving || !campaign}
                >
                  {loading || saving ? "Saving..." : "Update Campaign"}
                </Button>
              </div>
            }
          />
        </div>
      </div>

      <CampaignEditorForm
        ref={formRef}
        loading={loading}
        saving={saving}
        error={error}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

export default UpdateCampaignPage;
